/* eslint-disable react-hooks/exhaustive-deps */
import { yupResolver } from '@hookform/resolvers/yup';
import { Space } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { debounce, isNil } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { BaseCollapse } from '@/components/base-collapse/BaseCollapse';
import { ConfirmCancelEditing } from '@/components/confirm-popup';

import useFetch from '@/hooks/useFetch';

import { API } from '@/utils/constants/Apis';
import { handleCommonError } from '@/utils/helpers/common';
import { IForm } from '@/utils/interfaces/masterdata';
import { getForms } from '@/utils/services/master';

import { BaseButton } from '../../../components/base-button/BaseButton';
import { showBasePopup } from '../../../components/base-popup/BasePopup';
import { FormInputSelect } from '../../../components/form-input-select/FormInputSelect';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import useAuthorization, { C, CREATE_TEMPLATE_CANCEL, CREATE_TEMPLATE_SAVE, V } from '../../../hooks/useAuthorization';
import useOptionsGlobal from '../../../hooks/useOptionsGlobal';
import { setAlertNotification, setLoadingPage } from '../../../redux/globalReducer';
import { DEFAULT_PERPAGE_OPTIONS, SORT_LATEST, TYPE } from '../../../utils/constants/AppConstants';
import { TEMPLATE_LIST_URL, VIEW_TEMPLATE_URL } from '../../../utils/constants/RouteContants';
import {
  _generateFilterSearchQuery,
  _guuid,
  arrayToDict,
  intersectArrays,
  sanitizeAndRemoveInsecureLinks
} from '../../../utils/helpers/globalHelper';
import { IParams } from '../../../utils/interfaces/service';
import { ICategory, ITask } from '../../../utils/interfaces/template';
import { createTemplate, getDocumentsByCountryId, getTasksByCountryId, getTemplateById, updateTemplate } from '../../../utils/services/template';
import TitleButton from '../add-edit/components/title-button/TitleButton';
import { IInternalDocument, IRequiredDocument, IRequiredDocumentForm, ITemplateProcess, ITemplateProcessTask } from '../view/modules';
import { templateMTD } from './AddEditTemplateMapper';
import { FormTemplate } from './FormValidation';
import ReferenceMaterial from './components/reference-material/ReferenceMaterial';
import RequiredDocuments from './components/required-documents/RequiredDocuments';
import AddEditScheduleTemplate from './components/schedule/AddEditScheduleTemplate';
import TemplateTaskList from './components/template-task-list/TemplateTaskList';

import './AddEditTemplateLayout.scss';

export type DataChange = {
  schedules?: {
    schedulesDeleted: string[];
    indexChange: number | undefined;
    data?: any;
  };
  documents?: {
    documentsDeleted: string[];
    indexChange: number | undefined;
  };
  materials?: {
    materialsDeleted: string[];
    indexChange: number | undefined;
  };
  tasks?: {
    tasksDeleted: string[];
    indexChange: number | undefined;
  };
  version?: string;
};

export enum SUBMISSION_TO_MOT {
  NEED = 'need',
  UNNEED = 'unneed'
}

function AddEditTemplateLayout() {
  /**
   * Define security Props
   */
  const { hasPermission } = useAuthorization();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const formMethod = useForm({
    mode: 'all',
    shouldUnregister: false,
    resolver: yupResolver(FormTemplate)
  });
  const {
    formState: { isSubmitting }
  } = formMethod;
  // when create new data call function format data
  const { id } = useParams();
  const [createButton, setCreateButton] = useState(false);
  const [tasks, setTasks] = useState<ITask[]>([]);
  const dispatch = useAppDispatch();
  const [loadingBtn, setLoadingBtn] = useState<boolean>(false);
  const { otherCountry } = useOptionsGlobal();
  const [templateCountryId, setTemplateCountryId] = useState<string>('');
  const patterns = useAppSelector((state) => state.global.processPatterns);
  const { countryId, templateName, patternId, documents, materials } = formMethod.watch();
  const canSave = !!countryId && !!templateName?.trim() && !!patternId;
  const addEditTemplateRef = useRef<any>(null);
  const materialRef = useRef(null);
  const [formOptions, setFormOptions] = useState<DefaultOptionType[]>([]);
  const [optionsVisaCategory, setOptionsVisaCategory] = useState<DefaultOptionType[]>([]);

  const basicInformationRef = useRef<any>(null);
  const requireDocumentsRef = useRef<any>(null);
  const requireDocumentsDivRef = useRef<any>(null);
  const materialDivRef = useRef<any>(null);
  const taskListDivRef = useRef<any>(null);
  const [totalPagesDocuments] = useState<number>(0);
  const [paramsDocuments, setParamsDocuments] = useState<IParams>({
    pageSize: DEFAULT_PERPAGE_OPTIONS,
    pageIndex: -1,
    sorts: SORT_LATEST
  });
  const [taskOptions, setTaskOptions] = useState<DefaultOptionType[]>([]);
  const [tasksProcess, setTaskProcess] = useState<ITask[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState<boolean>(false);
  const [searchDocumentsKeywords, setSearchDocumentsKeywords] = useState<string>('');
  const [defaultDataProcess, setDefaultDataProcess] = useState<ITemplateProcess[]>([]);
  const [defaultInternalDocument, setDefaultInternalDocument] = useState<IInternalDocument[]>([]);
  const [defaultRequireDocument, setDefaultRequireDocument] = useState<IRequiredDocument[]>([]);
  const [defaultTemplateCategories, setDefaultTemplateCategories] = useState<IRequiredDocument[]>([]);
  const [referenceOriginId, setReferenceOriginId] = useState<string>('');

  useEffect(() => {
    if (id) {
      setReferenceOriginId(id);
    } else if (!referenceOriginId && countryId) {
      setReferenceOriginId(_guuid());
    }
  }, [id, countryId, referenceOriginId]);

  const { data: category } = useFetch<ICategory[] | null>(
    (countryId && referenceOriginId) || id ? API.GET_CATEGORY(countryId ?? '', 'TEMPLATE', id ?? referenceOriginId) : '',
    'GET'
  );

  const handleCheckErrorField = (field: string[], fieldRequire: string[]) => {
    const watchFieldsRequired = formMethod.watch(fieldRequire);
    const watchFieldsRequiredValid: any[] = watchFieldsRequired.filter((field: any) => field);
    const isEmptyRequireField = watchFieldsRequired?.length !== watchFieldsRequiredValid?.length;
    if (isEmptyRequireField) return true;
    if (fieldRequire?.includes('documents')) {
      const hasNullIdTask = documents?.some((doc: ITemplateProcess) => doc.templateProcessTasks?.some((task: ITemplateProcessTask) => !task.taskId));
      if (hasNullIdTask) {
        return true;
      }
    }
    if (fieldRequire?.includes('materials')) {
      const hasMaterialNullType = materials?.some((item: IRequiredDocumentForm) => !item?.typeId);
      if (hasMaterialNullType) {
        return true;
      }
    }
    if (fieldRequire?.includes('tasks')) {
      const hasTaskNullType = formMethod?.watch('tasks')?.some((item: IInternalDocument) => !item?.typeId);
      if (hasTaskNullType) {
        return true;
      }
    }
    const containsFormStateError = field?.some((item) => !!formMethod?.formState?.errors?.[item]);
    return !!containsFormStateError;
  };

  const fetchAssignmentForm = async () => {
    try {
      const { data } = await getForms();
      if (!data?.data) return;
      const newData = data.data.map((f: IForm) => ({
        ...f,
        label: f.name,
        value: f.id
      }));
      setFormOptions(newData);
    } catch (error) {}
  };
  const templateCategoriesToPayload = (data: any) => {
    const templateCategories: any[] = [];

    data.forEach((item: any) => {
      const rawValue = typeof item === 'object' ? item.value : item;
      const [categoryId, categoryName] = String(rawValue ?? '').split('::');
      const value = categoryId || rawValue;

      const found = optionsVisaCategory.find((c: any) => c.value === rawValue);

      const templateCategory = defaultTemplateCategories?.find((c: any) => c.categoryId === value);

      templateCategories.push({
        categoryId: value,
        categoryCode: item?.code || found?.code || found?.label,
        categoryName: item?.label || categoryName || found?.label,
        id: templateCategory?.id || _guuid(),
        deleted: false
      });
    });

    defaultTemplateCategories?.forEach((item: any) => {
      const isExistInData = templateCategories.some((d: any) => d.categoryId === item.categoryId);

      if (!isExistInData) {
        templateCategories.push({
          categoryId: item.categoryId,
          categoryCode: item.categoryCode,
          categoryName: item.categoryName,
          id: item.id,
          deleted: true
        });
      }
    });

    return { templateCategories };
  };

  // get template detail
  const getTemplateDetail = async (templateId: string = '', isFromChangeTemplate: boolean = false) => {
    const tempId = templateId || id;
    try {
      dispatch(setLoadingPage(true));
      const { data } = await getTemplateById(tempId as string);
      if (!data) return;
      const newInternalDocument = data?.internalDocuments?.map((item: IInternalDocument) => {
        return {
          ...item,
          note: sanitizeAndRemoveInsecureLinks(item?.note as string),
          numOfCopy: item?.numberOfCopy,
          typeId: item?.documentId
        };
      });
      const newRequiredDocument = data?.requiredDocuments?.map((item: IRequiredDocument) => {
        return {
          ...item,
          note: sanitizeAndRemoveInsecureLinks(item?.note as string),
          numOfCopy: item?.numberOfCopy,
          typeId: item?.documentId,
          submission: isNil(item?.submissionToMot) ? null : item?.submissionToMot ? SUBMISSION_TO_MOT.NEED : SUBMISSION_TO_MOT.UNNEED
        };
      });
      const newProcessData = (data?.templateProcesses ?? []).map((item: any) => ({
        ...item,
        templateProcessTasks: item.templateProcessTasks
          .filter((task: any) => [countryId, otherCountry?.id].includes(task?.countryId))
          .map((task: any) => ({
            ...task
          }))
      }));
      const newTemplateCategories = data?.templateCategories.map((item: any) => ({
        value: `${item.categoryId}::${item.categoryName}`,
        label: item.categoryName,
        code: item.categoryCode
      }));

      if (isFromChangeTemplate) {
        newInternalDocument.forEach((item: any) => {
          item.id = _guuid();
        });
        newRequiredDocument.forEach((item: any) => {
          item.id = _guuid();
        });
        newProcessData.forEach((item: any) => {
          item.id = _guuid();
          item.templateProcessTasks = item.templateProcessTasks
            .filter((task: any) => [countryId, otherCountry?.id].includes(task?.countryId))
            .map((task: any) => ({
              ...task,
              id: _guuid()
            }));
        });
        // setIsChangeTemplate(true);
        formMethod.setValue('materials', newRequiredDocument);
        formMethod.setValue('referanceTemplateId', templateId);
        formMethod.setValue('templateId', templateId);
        formMethod.setValue('tasks', newInternalDocument);
        formMethod.setValue('initDocuments', newProcessData);
        formMethod.setValue('patternId', data?.patternId);
        formMethod.setValue('templateCategories', newTemplateCategories);
      } else {
        await getDocumentOptions(data?.countryId);
        await fetchAssignmentForm();
        setDefaultDataProcess(data.templateProcesses);
        setDefaultRequireDocument(newRequiredDocument);
        setDefaultInternalDocument(newInternalDocument);
        setDefaultTemplateCategories(data?.templateCategories);
        formMethod.reset((pre) => {
          return {
            ...pre,
            ...data,
            countryId: data?.countryId,
            templateCategories: newTemplateCategories,
            templateName: data?.name,
            tasks: newInternalDocument,
            materials: newRequiredDocument,
            initDocuments: data.templateProcesses?.filter((item: ITemplateProcess) => item?.templateProcessTasks?.length),
            templateId: data?.referanceTemplateId,
            patternId: data?.patternId
          };
        });
      }
      setTemplateCountryId(data?.countryId);
      // trigger delay for run after line 91 useEffect ./RequiredDocuments.tsx done at first load
      setTimeout(() => formMethod.reset((pre) => ({ ...pre, isDirty: false })), 0);
    } catch (error) {
    } finally {
      dispatch(setLoadingPage(false));
    }
  };

  const [documentOptions, setDocumentOptions] = useState<DefaultOptionType[]>([]);
  const docRequestRef = useRef<number>(0);

  const handleSubmitForm = async () => {
    formMethod.handleSubmit(prepareSubmit(onSubmit), onError)();
  };

  const onError = async (err: any) => {
    const firstInvalidKey = Object.keys(err)?.[0];
    if (!firstInvalidKey) return;
    if (firstInvalidKey === 'templateName') return scrollIntoView(firstInvalidKey);

    const errorRecord = err[firstInvalidKey]?.find(Boolean);
    if (!errorRecord) return;

    if (firstInvalidKey === 'documents' && errorRecord?.templateProcessTasks) {
      const firstError = errorRecord.templateProcessTasks.find(Boolean);
      if (!firstError) return;

      const refName = firstError?.[Object.keys(firstError)[0]]?.ref?.name;
      return scrollIntoView(refName);
    }

    const keyError = Object.keys(errorRecord)?.[0];
    const refName = errorRecord?.[keyError]?.ref?.name;
    return scrollIntoView(refName);
  };

  const scrollIntoView = (elementId: string) => {
    if (!elementId) return;
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ block: 'center' });
      element.focus();
    }
  };

  const prepareSubmit = (next: typeof onSubmit) => {
    const dataInternalDocument = formMethod
      .getValues('tasks')
      ?.filter((task: IInternalDocument) => task.countryId)
      ?.map((item: any, index: number) => {
        return {
          id: item?.id,
          countryId: item?.countryId,
          documentId: item?.typeId,
          displayOrder: index + 1,
          numberOfCopy: item?.numOfCopy,
          note: item?.note,
          managementCode: item?.managementCode,
          version: Number(item?.version) || 0,
          changeDocumentFlg: !!item?.changeDocumentFlg
        };
      });
    const dataRequireDocument = formMethod
      .getValues('materials')
      ?.filter((req: IRequiredDocument) => req.countryId)
      ?.map((item: any, index: number) => {
        return {
          id: item?.id,
          deleted: item?.deleted,
          countryId: item?.countryId,
          documentId: item?.typeId,
          displayOrder: index + 1,
          numberOfCopy: item?.numOfCopy,
          note: item?.note,
          managementCode: item?.managementCode,
          submissionToMot: isNil(item?.submission) ? null : item?.submission === 'need',
          version: Number(item?.version) || 0,
          changeDocumentFlg: !!item?.changeDocumentFlg
        };
      });
    const { templateCategories, formId, templateName, templateId, patternId } = formMethod.getValues();
    const defaultProcessDict = arrayToDict(defaultDataProcess, 'id');
    const commonProcesses = intersectArrays(defaultDataProcess, documents as ITemplateProcess[], 'id');
    const commonProcessIdsSet = new Set(commonProcesses.map((i) => i.id));
    const dataTemplateProcessesUpdate = (documents as unknown as ITemplateProcess[]).reduce((arr: Partial<ITemplateProcess>[], item) => {
      if (commonProcessIdsSet.has(item.id)) {
        const defProcess = defaultProcessDict[item.id];
        const commonItems = intersectArrays(defProcess.templateProcessTasks, item.templateProcessTasks, 'id');
        const commonIdSet = new Set(commonItems.map((i) => i.id));
        const templateProcessTasks = [
          ...item?.templateProcessTasks?.map((i) => ({
            ...i,
            id: commonIdSet.has(i?.id) ? i.id : undefined,
            changeTaskFlg: !!i.changeTaskFlg
          })),
          ...defProcess?.templateProcessTasks
            ?.filter((i) => !commonIdSet.has(i.id))
            .map((i) => ({
              ...i,
              deleted: true
            }))
        ];
        const newItem: ITemplateProcess = {
          ...item,
          templateProcessTasks: templateProcessTasks as unknown as ITemplateProcessTask[]
        };
        arr.push(newItem);
      } else {
        arr.push({
          ...item,
          id: undefined,
          templateProcessTasks: item?.templateProcessTasks?.map((i) => ({ ...i, id: undefined, changeTaskFlg: true }))
        });
      }
      return arr;
    }, []);
    const dataTemplateProcessesDelete = defaultDataProcess
      .filter((i) => !commonProcessIdsSet.has(i.id))
      .map((item) => ({
        ...item,
        deleted: true,
        templateProcessTasks: item?.templateProcessTasks?.map((task) => ({ ...task, deleted: true }))
      }));
    const templateProcesses = [...dataTemplateProcessesUpdate, ...dataTemplateProcessesDelete];
    templateProcesses.forEach((item) => {
      if (!item?.templateProcessTasks) return;
      item.templateProcessTasks = item.templateProcessTasks.map((task, i) => ({ ...task, displayOrder: i }));
    });

    const dataPayload = {
      name: templateName,
      referanceTemplateId: templateId,
      patternId,
      countryId,
      formId,
      id: referenceOriginId,
      templateProcesses,
      requiredDocuments: handleDataSubmit(defaultRequireDocument, dataRequireDocument),
      internalDocuments: handleDataSubmit(defaultInternalDocument, dataInternalDocument),
      ...templateCategoriesToPayload(templateCategories)
    };

    return () => {
      next(dataPayload);
    };
  };

  const handleDataSubmit = (dataDefault: any[], dataUpdate: any[]) => {
    const commonItems = intersectArrays(dataUpdate, dataDefault, 'id');
    const commonIdSet = new Set(commonItems.map((i) => i.id));
    const dataInsertOrUpdate = dataUpdate.map((item: any) => ({
      ...item,
      id: commonIdSet.has(item.id) ? item.id : undefined,
      changeDocumentFlg: commonIdSet.has(item.id) ? !!item.changeDocumentFlg : true
    }));
    const dataDelete = dataDefault
      .filter((item: any) => !commonIdSet.has(item.id))
      .map((item: any) => ({
        ...item,
        deleted: true
      }));
    return [...dataInsertOrUpdate, ...dataDelete];
  };

  // Func submit template
  const onSubmit = async (values?: any) => {
    try {
      setLoadingBtn(true);
      dispatch(setLoadingPage(true));
      let res: any = {};
      const isEdit = !!id;
      if (id) {
        const dataChange = {
          ...values,
          id,
          version: formMethod?.getValues('version')
        };
        res = await updateTemplate(dataChange);
      } else {
        res = await onCreateTemplate(values);
      }
      let message: string = '';
      message = res && isEdit ? t('questionnaire:actions:save') : t('customer_request:create');
      formMethod.reset(undefined, { keepDirty: false, keepDirtyValues: false, keepValues: true });
      dispatch(
        setAlertNotification({
          show: true,
          type: 'success',
          position: 'top',
          message: t('common:MSG_C_003', { item: message })
        })
      );
      const projectId = isEdit ? id : res?.data;
      setTimeout(() => {
        return navigate(VIEW_TEMPLATE_URL(projectId));
      }, 0);
    } catch (error: any) {
      if (error?.response) {
        handleCommonError(error);
      }
    } finally {
      dispatch(setLoadingPage(false));
      setLoadingBtn(false);
    }
  };

  // Func create template
  const onCreateTemplate = async (values: any) => {
    return new Promise(async (resolve, reject) => {
      try {
        const dataCreate = templateMTD(values);
        const templateResponse = await createTemplate({ ...dataCreate });
        resolve({
          message: t('common:MSG_026'),
          data: templateResponse?.data
        });
      } catch (error: any) {
        reject(error);
      }
    });
  };

  // Func cancel
  const onCancel = () => {
    navigate(id ? VIEW_TEMPLATE_URL(id) : TEMPLATE_LIST_URL);
  };

  const delayedHandleSearch = debounce((keyword: string) => {
    if (keyword) {
      setDocumentOptions([]);
      setTasks([]);
      const querySearch = _generateFilterSearchQuery(['nameSearch'], keyword);
      setParamsDocuments({
        ...paramsDocuments,
        pageIndex: 0,
        filter: JSON.stringify(querySearch)
      });
    } else {
      setParamsDocuments({
        ...paramsDocuments,
        pageIndex: 0,
        filter: undefined
      });
    }
  }, 300);

  const onHandleSearchDocuments = (keyword: string) => {
    setSearchDocumentsKeywords(keyword);
    setLoadingDocuments(true);
    delayedHandleSearch(keyword);
  };

  const getDocumentOptions = async (countryId: string) => {
    try {
      if (!countryId) return;
      docRequestRef.current += 1;
      const reqId = docRequestRef.current;
      setLoadingDocuments(true);
      setDocumentOptions([]);
      setTasks([]);
      setTaskOptions([]);
      setTaskProcess([]);

      const { data } = await getDocumentsByCountryId(countryId as string);
      const task = await getTasksByCountryId(countryId as string);
      if (reqId !== docRequestRef.current) return;
      if (data?.length) {
        const newDocumentOptions = data?.map((item: any) => {
          return { label: item?.managementCode, value: item?.id, name: item?.name };
        });
        setTasks(data);
        setDocumentOptions(newDocumentOptions);
      } else {
        setTasks([]);
        setDocumentOptions([]);
      }
      if (task?.data?.length) {
        const newTasksOptions = task?.data?.map((item: any) => {
          return { label: item?.managementCode, value: item?.id, type: item?.type, renderValue: item?.name };
        });
        setTaskOptions(newTasksOptions);
        setTaskProcess(task?.data);
      } else {
        setTaskOptions([]);
        setTaskProcess([]);
      }
      setLoadingDocuments(false);
    } catch (e) {}
  };

  const handleSelectPattern = async (pattern: string) => {
    let isChangeData: boolean = false;
    if (!documents?.length) return true;
    documents?.forEach((item: any) => {
      item?.templateProcessTasks?.forEach((task: any) => {
        if (task) isChangeData = true;
      });
    });
    if (!isChangeData) {
      // setIsChangeTemplate(true);
      return true;
    }
    const show = await showBasePopup({
      type: TYPE.CONFIRM,
      title: t('common:MSG_C_013.title') || '',
      msg: t('common:MSG_C_013.description') ?? ''
    });
    if (show === TYPE.CONFIRM) {
      // setIsChangeTemplate(true);
      const newDocument = documents?.map((item: any) => {
        return { ...item, templateProcessTasks: [] };
      });
      formMethod.reset((prev) => {
        return { ...prev, documents: pattern ? newDocument : [], initDocuments: [] };
      });
      return true;
    }
    return false;
  };
  const watchFieldsScheduleTemplate = formMethod.watch(['countryId', 'templateCategories', 'formId', 'templateName', 'templateId']);
  const watchFieldsValidScheduleTemplate = watchFieldsScheduleTemplate.filter((field: any) => field);

  useEffect(() => {
    if (!category?.length) return;
    dispatch(setLoadingPage(true));
    if (id) {
      getTemplateDetail();
      setCreateButton(false);
    } else {
      otherCountry && getDocumentOptions(otherCountry?.id as string);
      setCreateButton(false);
      fetchAssignmentForm();
      if (window.location.pathname.includes('add')) {
        setCreateButton(true);
      }
    }
    setTimeout(() => {
      dispatch(setLoadingPage(false));
    }, 1000);
  }, [id, category]);

  useEffect(() => {
    if (otherCountry) {
      setParamsDocuments({
        ...paramsDocuments,
        pageIndex: 0
      });
    }
  }, [templateCountryId, formMethod.getValues('refId')]);

  // Catch countryId changes
  useEffect(() => {
    if (!category || !countryId) return;
    const newCategoryOptions = category.map((c: ICategory) => ({
      value: `${c.id}::${c.name}`,
      label: c.name,
      code: c.code
    }));
    setOptionsVisaCategory(newCategoryOptions);
    // Filter old data with current country
    if (countryId && formMethod?.formState?.isDirty) {
      getDocumentOptions(countryId);
      fetchAssignmentForm();
      const newTasks = (formMethod.getValues('tasks') ?? [])
        .filter((item: any) => [countryId, otherCountry?.id].includes(item?.countryId))
        .map((item: IInternalDocument) => {
          return {
            ...item,
            note: sanitizeAndRemoveInsecureLinks(item?.note as string),
            numOfCopy: item?.numberOfCopy,
            typeId: item?.documentId
          };
        });
      const newMaterials = (formMethod.getValues('materials') ?? [])
        .filter((item: any) => [countryId, otherCountry?.id].includes(item?.countryId))
        .map((item: IRequiredDocument) => {
          return {
            ...item,
            note: sanitizeAndRemoveInsecureLinks(item?.note as string),
            numOfCopy: item?.numberOfCopy,
            typeId: item?.documentId,
            submission: isNil(item?.submissionToMot) ? null : item?.submissionToMot ? SUBMISSION_TO_MOT.NEED : SUBMISSION_TO_MOT.UNNEED
          };
        });
      const newDocuments = (formMethod?.getValues('documents') ?? []).map((item: any) => ({
        ...item,
        id: _guuid(),
        templateProcessTasks: item.templateProcessTasks
          .filter((task: any) => [countryId, otherCountry?.id].includes(task?.countryId))
          .map((task: any) => ({
            ...task,
            id: _guuid()
          }))
      }));
      formMethod.setValue('materials', newMaterials);
      formMethod.setValue('tasks', newTasks);
      formMethod.setValue('documents', newDocuments);
      formMethod.setValue('templateCategories', []);
      formMethod.setValue('templateId', '');
    }
  }, [countryId, category]);

  const getTitle = (): string => {
    return id ? t('sider:template_edit') : t('template_layout:template_create');
  };
  return (
    <div className='container h-full add-edit-template-layout-wrapper'>
      <div className='template-header'>
        <TitleButton edit={true} create={createButton} title={getTitle()}>
          {hasPermission(CREATE_TEMPLATE_CANCEL, V) && (
            <BaseButton
              type='tertiary'
              size='medium'
              disabled={!hasPermission(CREATE_TEMPLATE_CANCEL, C)}
              onClick={(e) => {
                onCancel();
              }}
            >
              <div> {t('template_layout:cancel_button')} </div>
            </BaseButton>
          )}
          {hasPermission(CREATE_TEMPLATE_SAVE, V) && (
            <BaseButton
              type='primary'
              size='medium'
              disabled={!hasPermission(CREATE_TEMPLATE_SAVE, C) || !canSave || isSubmitting}
              onClick={handleSubmitForm}
              loading={loadingBtn}
            >
              <div>{t('template_layout:save_button')}</div>
            </BaseButton>
          )}
        </TitleButton>
      </div>

      <FormProvider {...formMethod}>
        <div
          className='template-layout edit-template-content container-scroll px-5 py-4 rounded-[10px] border-[#DBDBE0] gap-3'
          ref={addEditTemplateRef}
        >
          <BaseCollapse
            id='title_basic_information'
            title={t('template_layout:title_basic_information')}
            total={5}
            questions={watchFieldsValidScheduleTemplate?.length}
            isError={handleCheckErrorField(['countryId', 'templateName'], ['countryId', 'templateName'])}
            ref={basicInformationRef}
          >
            <div className='add-schedule'>
              <AddEditScheduleTemplate
                getDocumentOptions={getDocumentOptions}
                name='schedules'
                method={formMethod}
                getTemplateDetail={getTemplateDetail}
                fetchAssignmentForm={fetchAssignmentForm}
                optionsVisaCategory={optionsVisaCategory}
                formOptions={formOptions}
                referenceOriginId={referenceOriginId}
              />
            </div>
          </BaseCollapse>
          <BaseCollapse
            id='procedual_process'
            title={t('template_layout:procedual_process')}
            total={1}
            questions={formMethod.watch(['patternId']).filter((field: any) => field).length || 0}
            isError={handleCheckErrorField(['patternId', 'documents'], ['patternId', 'documents'])}
            ref={requireDocumentsDivRef}
          >
            <div>
              <div>
                <Space className='mb-2'>
                  <FormInputSelect
                    disabled={!countryId}
                    required
                    name='patternId'
                    labelTx='template_layout:procedual_process'
                    placeholder={t('placeholder:select', { field: t('template_layout:procedual_process') }) as string}
                    isColumn={true}
                    options={patterns?.map((e) => ({ label: `${e.name}：${e.description}`, value: e.id }))}
                    className='!w-[565px]'
                    allowClear={false}
                    isConfirmPopup={true}
                    handleSelect={(value: any) => {
                      return handleSelectPattern(value);
                    }}
                  />
                </Space>
              </div>
              <RequiredDocuments
                ref={requireDocumentsRef}
                name='documents'
                documents={tasksProcess}
                documentOptions={taskOptions}
                control={formMethod.control}
                formMethod={formMethod}
                loadingData={loadingDocuments}
                onHandleSearchDocuments={(keyword: string) => onHandleSearchDocuments(keyword)}
                onPopupScroll={() => {
                  if (!loadingDocuments && paramsDocuments.pageIndex < totalPagesDocuments - 1) {
                    setParamsDocuments({
                      ...paramsDocuments,
                      pageIndex: ++paramsDocuments.pageIndex
                    });
                  }
                }}
              />
            </div>
          </BaseCollapse>
          <BaseCollapse
            id='preparation_documents'
            title={t('template_layout:preparation_documents')}
            isError={handleCheckErrorField(['materials'], ['materials'])}
            ref={materialDivRef}
          >
            <div>
              <ReferenceMaterial
                name='materials'
                documents={tasks}
                documentOptions={documentOptions}
                control={formMethod.control}
                formMethod={formMethod}
                ref={materialRef}
                loadingData={loadingDocuments}
                refreshSearchWhenOpen={!!searchDocumentsKeywords}
                onHandleSearchDocuments={(keyword: string) => onHandleSearchDocuments(keyword)}
                onPopupScroll={() => {
                  if (!loadingDocuments && paramsDocuments.pageIndex < totalPagesDocuments - 1) {
                    setParamsDocuments({
                      ...paramsDocuments,
                      pageIndex: ++paramsDocuments.pageIndex
                    });
                  }
                }}
                disabled={!countryId}
              />
            </div>
          </BaseCollapse>
          <BaseCollapse
            id='task_list'
            title={t('template_layout:document')}
            isError={handleCheckErrorField(['tasks'], ['tasks'])}
            ref={taskListDivRef}
          >
            <div>
              <TemplateTaskList
                name='tasks'
                task={tasks}
                documentOptions={documentOptions}
                control={formMethod.control}
                formMethod={formMethod}
                loadingData={loadingDocuments}
                refreshSearchWhenOpen={!!searchDocumentsKeywords}
                onHandleSearchDocuments={(keyword: string) => onHandleSearchDocuments(keyword)}
                onPopupScroll={() => {
                  if (!loadingDocuments && paramsDocuments.pageIndex < totalPagesDocuments - 1) {
                    setParamsDocuments({
                      ...paramsDocuments,
                      pageIndex: ++paramsDocuments.pageIndex
                    });
                  }
                }}
                disabled={!countryId}
              />
            </div>
          </BaseCollapse>
        </div>
      </FormProvider>
      <ConfirmCancelEditing condition={formMethod?.formState?.isDirty && !formMethod?.formState?.isSubmitSuccessful} />
    </div>
  );
}

export default AddEditTemplateLayout;
